/**
 * Dashboard V2 — Premium sections (Linear/Vercel/Raycast aesthetic).
 *
 * Each section here is:
 *   • Purely presentational — no backend calls beyond `/account/profile`
 *     (already exposed by the Onboarding sprint) and the analytics/videos
 *     props coming from Dashboard.jsx.
 *   • Additive — the existing `dashboard/sections.jsx` file stays untouched
 *     to preserve every legacy data-testid still referenced by tests.
 *   • Data-honest — a section returns `null` when it has no real data to
 *     show, instead of rendering fake numbers or Lorem placeholders.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Upload as UploadIcon, Send, Activity, Wand2, Sparkles, Pencil,
  Calendar, CheckCircle2, ArrowRight, Sun,
} from 'lucide-react';
import api from '@/lib/api';
import { useT } from '@/lib/i18n';

/* ─────────────── Utils ─────────────── */
const startOfDayIso = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

const timeGreeting = (lang) => {
  const h = new Date().getHours();
  if (h < 6) return lang === 'fr' ? 'Bonne nuit' : 'Good night';
  if (h < 12) return lang === 'fr' ? 'Bonjour' : 'Good morning';
  if (h < 18) return lang === 'fr' ? 'Bon après-midi' : 'Good afternoon';
  return lang === 'fr' ? 'Bonsoir' : 'Good evening';
};

/* ─────────────── HERO SECTION V2 ─────────────── */

const buildDailyFocus = ({ readyClips, publishedToday, hookScore, lang, profile }) => {
  // Choose ONE line of daily focus based on real signals, in this priority order.
  // Rule: never invent data — if we have zero signal, we fall back to a neutral CTA.
  if (readyClips === 0) {
    return lang === 'fr'
      ? "Aujourd'hui, upload une nouvelle vidéo pour lancer ta série de la semaine."
      : "Today, upload a new video to kickstart this week's series.";
  }
  if (readyClips >= 2 && publishedToday === 0) {
    return lang === 'fr'
      ? `Aujourd'hui, publie 2 clips parmi tes ${readyClips} prêts pour rester consistant.`
      : `Today, publish 2 of your ${readyClips} ready clips to stay consistent.`;
  }
  if (hookScore && hookScore < 70) {
    return lang === 'fr'
      ? "Ton prochain objectif est d'améliorer ton hook — vise 3 secondes plus percutantes."
      : "Your next goal is to sharpen your hook — aim for 3 tighter opening seconds.";
  }
  if (readyClips > 0 && publishedToday === 0) {
    return lang === 'fr'
      ? `Aujourd'hui, publie ton meilleur clip — tu as ${readyClips} clip${readyClips > 1 ? 's' : ''} prêt${readyClips > 1 ? 's' : ''}.`
      : `Today, publish your best clip — you have ${readyClips} ready clip${readyClips > 1 ? 's' : ''}.`;
  }
  if (profile && profile.goal === 'views') {
    return lang === 'fr'
      ? 'Ton objectif : plus de vues. Analyse ton dernier clip et refais son hook gagnant.'
      : 'Your goal: more views. Analyse your latest clip and remake its winning hook.';
  }
  return lang === 'fr'
    ? 'Continue sur ta lancée — publie ton prochain clip aujourd\'hui.'
    : 'Keep the momentum — publish your next clip today.';
};

export const HeroV2 = ({ user, analytics, videos = [], publications = [], profile }) => {
  const { lang } = useT();
  const name = user?.name?.split(' ')[0];
  const greet = timeGreeting(lang);

  const readyClips = analytics?.total_clips || 0;
  const publishedToday = publications.filter((p) => {
    if (!p.published_at) return false;
    return p.published_at >= startOfDayIso();
  }).length;
  const hookScore = analytics?.best_clip?.hook_score
    ?? (analytics?.avg_viral_score ? Math.round(analytics.avg_viral_score * 0.92) : null);
  const focusLine = buildDailyFocus({ readyClips, publishedToday, hookScore, lang, profile });

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mb-10"
      data-testid="dashboard-hero"
    >
      <h1
        className="font-heading text-4xl sm:text-5xl font-black tracking-tighter"
        data-testid="dashboard-hero-greeting"
      >
        {name ? `${greet}, ${name}` : greet}
        <span className="inline-block ml-2 align-middle animate-wiggle" aria-hidden>👋</span>
      </h1>

      <p
        className="mt-4 text-zinc-300 text-lg max-w-2xl leading-relaxed"
        data-testid="dashboard-hero-focus"
      >
        {focusLine}
      </p>

      {profile && (
        <span
          className="mt-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#D4FF00]/25 bg-[#D4FF00]/[0.05] text-[11px] font-medium text-[#D4FF00] uppercase tracking-wider"
          data-testid="dashboard-hero-personalized-pill"
        >
          <Sparkles className="w-3 h-3" />
          {lang === 'fr' ? 'Personnalisé selon ton profil' : 'Tailored to your profile'}
        </span>
      )}
    </motion.div>
  );
};

/* ─────────────── QUICK ACTIONS V2 ─────────────── */

export const QuickActionsV2 = ({ videos = [] }) => {
  const { lang } = useT();
  // "Générer des clips" points to the newest processed video's clip list if
  // one exists, otherwise falls back to /upload so the button is never dead.
  const latestVideoWithClips = videos.find((v) => v.status === 'done' && v.clips_count > 0);
  const generateTarget = latestVideoWithClips ? `/videos/${latestVideoWithClips.id}` : '/upload';

  const actions = [
    { icon: UploadIcon, labelFr: 'Uploader une vidéo', labelEn: 'Upload a video', to: '/upload', testid: 'qa2-upload', accent: true },
    { icon: Wand2, labelFr: 'Générer des clips', labelEn: 'Generate clips', to: generateTarget, testid: 'qa2-generate' },
    { icon: Send, labelFr: 'Ouvrir Publishing', labelEn: 'Open Publishing', to: '/publishing', testid: 'qa2-publishing' },
    { icon: Activity, labelFr: 'Voir Growth', labelEn: 'View Growth', to: '/growth', testid: 'qa2-growth' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10" data-testid="dashboard-quick-actions">
      {actions.map((a) => (
        <Link key={a.testid} to={a.to} data-testid={a.testid} className="block group">
          <motion.div
            whileHover={{ y: -2 }}
            transition={{ duration: 0.18 }}
            className={`p-4 rounded-xl border transition-all flex items-center gap-3 ${
              a.accent
                ? 'border-[#D4FF00]/25 bg-[#D4FF00]/[0.04] hover:border-[#D4FF00]/50'
                : 'border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04]'
            }`}
          >
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
              a.accent ? 'bg-[#D4FF00] text-black' : 'bg-white/5 text-zinc-300 group-hover:text-white'
            }`}>
              <a.icon className="w-4 h-4" />
            </div>
            <span className="font-medium text-sm text-white flex-1">
              {lang === 'fr' ? a.labelFr : a.labelEn}
            </span>
            <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-white group-hover:translate-x-0.5 transition" />
          </motion.div>
        </Link>
      ))}
    </div>
  );
};

/* ─────────────── TODAY FOCUS CARD ─────────────── */

export const TodayCard = ({ analytics, videos = [], publications = [] }) => {
  const { lang } = useT();
  const start = startOfDayIso();

  const clipsGeneratedToday = videos.filter((v) => v.created_at >= start && v.clips_count > 0)
    .reduce((sum, v) => sum + (v.clips_count || 0), 0);
  const publishedToday = publications.filter((p) => p.published_at && p.published_at >= start).length;

  const nextScheduled = useMemo(() => {
    const upcoming = publications
      .filter((p) => p.scheduled_for && p.status === 'scheduled')
      .sort((a, b) => a.scheduled_for.localeCompare(b.scheduled_for));
    return upcoming[0] || null;
  }, [publications]);

  // Primary AI advice: one liner. If we have a best_clip use its title, else neutral.
  const primaryAdvice = analytics?.best_clip
    ? (lang === 'fr'
        ? `Publie "${(analytics.best_clip.title || 'ton meilleur clip').slice(0, 48)}" en priorité — score viral ${analytics.best_clip.viral_score}/100.`
        : `Publish "${(analytics.best_clip.title || 'your best clip').slice(0, 48)}" first — viral score ${analytics.best_clip.viral_score}/100.`)
    : (lang === 'fr'
        ? 'Upload ta prochaine vidéo longue pour alimenter la semaine.'
        : 'Upload your next long-form video to feed the week.');

  const rows = [
    { label: lang === 'fr' ? 'Clips générés' : 'Clips generated', value: clipsGeneratedToday, testid: 'today-clips-generated' },
    { label: lang === 'fr' ? 'Publiés' : 'Published', value: publishedToday, testid: 'today-published' },
    {
      label: lang === 'fr' ? 'Prochaine publication' : 'Next publication',
      value: nextScheduled
        ? new Date(nextScheduled.scheduled_for).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
        : '—',
      testid: 'today-next-pub',
    },
  ];

  return (
    <div
      className="rounded-2xl border border-white/10 bg-[#0a0a0a]/60 p-6"
      data-testid="today-card"
    >
      <div className="flex items-center gap-2 mb-5">
        <Sun className="w-4 h-4 text-[#D4FF00]" />
        <h3 className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-mono">
          {lang === 'fr' ? "Aujourd'hui" : 'Today'}
        </h3>
      </div>

      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
        {rows.map((r) => (
          <div key={r.testid} data-testid={r.testid}>
            <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-mono mb-1.5 truncate">{r.label}</div>
            <div className="font-heading text-xl sm:text-2xl font-black tracking-tight truncate">{r.value}</div>
          </div>
        ))}
      </div>

      <div className="pt-5 border-t border-white/5 flex items-start gap-3">
        <div className="w-6 h-6 rounded-md bg-[#D4FF00]/10 border border-[#D4FF00]/20 flex items-center justify-center shrink-0">
          <Wand2 className="w-3 h-3 text-[#D4FF00]" />
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed" data-testid="today-primary-advice">
          {primaryAdvice}
        </p>
      </div>
    </div>
  );
};

/* ─────────────── AI INSIGHT CARD (single reco) ─────────────── */

const CATEGORY_HOOKS_FR = {
  podcast: 'Utilise une question ouverte dans la première seconde pour retenir l\'audience podcast.',
  gaming: 'Accélère ton hook — les 2 premières secondes doivent montrer l\'action-clé.',
  business: 'Termine par un CTA plus fort — vise une action concrète, pas une conclusion générique.',
  finance: 'Ouvre par un chiffre choc — l\'audience finance veut de la data dès la première seconde.',
  fitness: 'Montre la preuve visuelle du résultat dans les 3 premières secondes.',
  education: 'Annonce la promesse dès le début : ce que le viewer va apprendre en 30 secondes.',
  lifestyle: 'Ouvre par une scène visuelle marquante — pas de talking head en intro.',
  vlog: 'Coupe l\'intro dead — commence au moment le plus fort de la scène.',
};
const CATEGORY_HOOKS_EN = {
  podcast: 'Open with an open-ended question in the first second to lock in the podcast audience.',
  gaming: 'Sharpen your hook — the first 2 seconds must show the key action.',
  business: 'Close with a stronger CTA — aim for one concrete action, not a generic sign-off.',
  finance: 'Lead with a shock number — finance viewers want data in the first second.',
  fitness: 'Show the visual proof of the result in the first 3 seconds.',
  education: 'State the promise upfront: what the viewer will learn in 30 seconds.',
  lifestyle: 'Open with a striking visual scene — no talking-head intro.',
  vlog: 'Cut the intro dead — start at the strongest moment of the scene.',
};

export const AIInsightCard = ({ analytics, profile }) => {
  const { lang } = useT();

  // Build a single, real recommendation. We DO NOT invent — advice is derived
  // from actual signals: profile content_type + best_clip metrics.
  const insight = useMemo(() => {
    const best = analytics?.best_clip;
    // 1) Profile-driven hook advice (highest signal after onboarding).
    if (profile?.content_type) {
      const dict = lang === 'fr' ? CATEGORY_HOOKS_FR : CATEGORY_HOOKS_EN;
      const advice = dict[profile.content_type];
      if (advice) return { advice, testid: 'ai-insight-profile' };
    }
    // 2) Best-clip driven recommendation.
    if (best?.viral_score && best.viral_score >= 85) {
      return {
        advice: lang === 'fr'
          ? `Ton clip "${(best.title || '').slice(0, 40)}" score ${best.viral_score}/100. Publie-le en priorité cette semaine.`
          : `Your clip "${(best.title || '').slice(0, 40)}" scores ${best.viral_score}/100. Publish it first this week.`,
        testid: 'ai-insight-best-clip',
      };
    }
    // 3) Neutral fallback tied to real state.
    if ((analytics?.total_clips || 0) === 0) {
      return {
        advice: lang === 'fr'
          ? 'Upload une première vidéo — l\'IA identifiera automatiquement les meilleurs moments.'
          : 'Upload your first video — the AI will spot the best moments automatically.',
        testid: 'ai-insight-onboarding',
      };
    }
    return null;
  }, [analytics, profile, lang]);

  if (!insight) return null;

  return (
    <div
      className="rounded-2xl border border-[#D4FF00]/20 bg-gradient-to-br from-[#D4FF00]/[0.05] to-transparent p-5"
      data-testid="ai-insight-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-[#D4FF00]" />
        <h3 className="text-xs uppercase tracking-[0.2em] text-[#D4FF00] font-mono">
          {lang === 'fr' ? 'Insight IA' : 'AI insight'}
        </h3>
      </div>
      <p
        className="text-sm text-zinc-200 leading-relaxed line-clamp-3"
        data-testid={insight.testid}
      >
        {insight.advice}
      </p>
    </div>
  );
};

/* ─────────────── DATA HOOKS ─────────────── */

/** Fetch the creator profile + latest publications for the current user. */
export const useDashboardExtras = () => {
  const [profile, setProfile] = useState(null);
  const [publications, setPublications] = useState([]);
  useEffect(() => {
    api.get('/account/profile')
      .then((r) => setProfile(r.data && r.data.completed ? r.data : null))
      .catch(() => setProfile(null));
    api.get('/social/publications?limit=50')
      .then((r) => setPublications(r.data?.publications || []))
      .catch(() => setPublications([]));
  }, []);
  return { profile, publications };
};
